import type React from 'react'
import { useEffect, useState } from 'react'
import { useStream } from '@langchain/langgraph-sdk/react'
import type { Message } from '@langchain/langgraph-sdk'
import type { Conversation, ConversationPlan } from '@knownow/domain'
import { SupabaseConversationRepository } from '@knownow/conversation'
import { ChatBubble } from '@knownow/ui'
import { MarkdownContent } from '@/components/markdown-content'
import { getMessageText } from '@/lib/message-utils'
import { useAuthenticatedClient } from '@/hooks/use-authenticated-client'
import { supabase } from '@/lib/supabase'
import { ConversationSummaryPanel } from './conversation-summary-panel'

const AGENT_URL =
  (import.meta.env['VITE_AGENT_URL'] as string | undefined) ?? 'http://127.0.0.1:3001'

const COMPLETION_MARKER = '__CONVERSATION_COMPLETE__'

export interface ConversationDrillInProps {
  readonly conversationId: string
  readonly plan: ConversationPlan
}

export function ConversationDrillIn({ conversationId, plan }: ConversationDrillInProps): React.ReactElement {
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const { client, isReady } = useAuthenticatedClient(`${AGENT_URL}/interview`)

  useEffect(() => {
    const repo = new SupabaseConversationRepository(supabase, plan.organizationId)
    repo
      .getById(conversationId)
      .then((c) => setConversation(c))
      .catch(() => setConversation(null))
  }, [conversationId, plan.organizationId])

  const thread = useStream({
    client,
    assistantId: 'interview-agent',
    threadId: isReady ? conversationId : null,
  })

  const visibleMessages = thread.messages.filter(
    (m: Message) => m.type === 'human' || m.type === 'ai',
  )

  return (
    <div className="flex h-full" data-testid="drill-in-container">
      <div className="flex-1 overflow-y-auto p-4 border-r border-neutral-200">
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Transcript</h3>

        {thread.isLoading && (
          <p className="text-sm text-neutral-500">Loading transcript...</p>
        )}

        <div className="flex flex-col gap-3">
          {visibleMessages.map((msg: Message, i: number) => {
            const text = getMessageText(msg)
            if (text === COMPLETION_MARKER) return null
            const isAi = msg.type === 'ai'
            return (
              <ChatBubble key={msg.id ?? i} variant={isAi ? 'ai' : 'user'}>
                {isAi ? <MarkdownContent content={text} /> : text}
              </ChatBubble>
            )
          })}
        </div>
      </div>

      <div className="w-[28rem] shrink-0 border-l border-neutral-200">
        <ConversationSummaryPanel
          plan={plan}
          conversationId={conversationId}
          organizationId={plan.organizationId}
          canSummarize={conversation?.status === 'completed'}
        />
      </div>
    </div>
  )
}
